/**
 * @fileoverview Favourites facade — unified UI over native or ls-favourites provider
 * @module Favourites
 */

(function() {
  'use strict';

  const COUNT_COOKIE = 'ls_favourites_count';
  const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
  const config = window.LS_FAVOURITES_CONFIG || { backend: 'native', currency: 'GBP' };
  const productOpChains = new Map();
  let pdpSocialCount = null;

  function getProvider() {
    if (config.backend === 'ls-favourites') {
      return window.FavouritesLSProvider;
    }
    return window.FavouritesNativeProvider;
  }

  function usesOptimisticProvider() {
    const provider = getProvider();
    return Boolean(provider && config.backend === 'ls-favourites' && provider.optimisticAdd);
  }

  function getCookieDomain() {
    return location.hostname.endsWith('label-source.co.uk')
      ? '; domain=.label-source.co.uk'
      : '';
  }

  function syncCountCookie(count) {
    const base = `${COUNT_COOKIE}=${count}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${getCookieDomain()}`;
    document.cookie = location.protocol === 'https:' ? `${base}; Secure` : base;
  }

  function normalizeProductId(productId) {
    const id = String(productId);
    return id.includes('/') ? id.split('/').pop() : id;
  }

  function dispatchChange() {
    const event = new CustomEvent('favourites:change', {
      detail: {
        count: Favourites.count(),
        ids: Favourites.getAll()
      }
    });
    document.dispatchEvent(event);
  }

  function getPdpData() {
    const el = document.getElementById('pdp-favourite-data');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error('[Favourites] Invalid PDP favourite data:', error);
      return null;
    }
  }

  function getPdpVariantId(pdpData) {
    return pdpData?.variantId;
  }

  function parsePriceCents(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
  }

  function getCardContext(card) {
    const link = card.closest('.product-card-wrapper')?.querySelector('[data-product-url]');
    const handle = card.dataset.productHandle;
    const productUrl = link?.getAttribute('href') || (handle ? '/products/' + handle : null);
    const context = {
      type: 'card',
      handle: handle || null,
      productUrl: productUrl
    };

    if (config.backend === 'ls-favourites') {
      context.sku = card.dataset.productSku || null;
      context.productSku = context.sku;
      context.priceCents = parsePriceCents(card.dataset.productPriceCents);
      context.currency = config.currency || 'GBP';
    }

    return context;
  }

  function getGtmContextFromCard(card, numericId) {
    const productName = card.querySelector('[data-product-title]')?.textContent?.trim() || '';
    const productVendor = card.querySelector('[data-product-vendor]')?.textContent?.trim() || '';
    const priceElement = card.querySelector('[data-product-price]');
    let productPrice = 0;
    if (priceElement) {
      const priceText = priceElement.textContent.replace(/[^0-9.]/g, '');
      productPrice = parseFloat(priceText) || 0;
    }
    return {
      item_id: card.dataset.productSku || numericId,
      item_name: productName,
      item_brand: productVendor,
      price: productPrice,
      value: productPrice
    };
  }

  function getGtmContextFromPdp(pdpData) {
    if (!pdpData) return null;
    return {
      item_id: pdpData.sku || String(pdpData.productId),
      item_name: pdpData.title || '',
      item_brand: pdpData.vendor || '',
      item_category: pdpData.type || '',
      price: pdpData.price || 0,
      value: pdpData.price || 0
    };
  }

  function pushAddToWishlistGTM(gtmItem) {
    if (!gtmItem) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'add_to_wishlist',
      ecommerce: {
        currency: config.currency || 'GBP',
        value: gtmItem.value,
        items: [{
          item_id: gtmItem.item_id,
          item_name: gtmItem.item_name,
          item_brand: gtmItem.item_brand,
          item_category: gtmItem.item_category,
          price: gtmItem.price,
          quantity: 1
        }]
      }
    });
  }

  function isFavourited(productId) {
    return Favourites.has(normalizeProductId(productId));
  }

  function setButtonState(btn, isFavourited) {
    btn.classList.toggle('is-favourited', isFavourited);
    btn.setAttribute('aria-pressed', isFavourited ? 'true' : 'false');
    btn.setAttribute('aria-label', isFavourited ? 'Remove from favourites' : 'Add to favourites');
  }

  function getProductKey(productId, variantId) {
    return normalizeProductId(productId) + ':' + (variantId != null ? String(variantId) : '');
  }

  function isButtonBusy(btn) {
    return btn.dataset.favouritePending === 'true';
  }

  function setButtonBusy(btn, busy) {
    if (busy) {
      btn.dataset.favouritePending = 'true';
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-busy');
    } else {
      delete btn.dataset.favouritePending;
      btn.removeAttribute('aria-disabled');
      btn.classList.remove('is-busy');
    }
  }

  function setButtonsLoading(loading) {
    // PDP button is excluded — social count is hydrated from Typesense; clicks still await provider.ready.
    document.querySelectorAll('.product-card-wrapper .product-favourite-btn').forEach((btn) => {
      if (loading) {
        btn.classList.add('is-loading');
        if (!isButtonBusy(btn)) {
          btn.setAttribute('aria-disabled', 'true');
        }
        return;
      }
      btn.classList.remove('is-loading');
      if (!isButtonBusy(btn)) {
        btn.removeAttribute('aria-disabled');
      }
    });
  }

  function enqueueProductOp(key, fn) {
    const previous = productOpChains.get(key) || Promise.resolve();
    const next = previous.catch(() => {}).then(fn);
    productOpChains.set(key, next);
    return next.finally(() => {
      if (productOpChains.get(key) === next) {
        productOpChains.delete(key);
      }
    });
  }

  function removeCardFromFavouritesPage(cardWrapper) {
    if (!cardWrapper || !document.querySelector('#favourites-product-grid')) return;

    cardWrapper.style.transition = 'opacity 0.3s ease-out, transform 0.3s ease-out';
    cardWrapper.style.opacity = '0';
    cardWrapper.style.transform = 'scale(0.95)';

    setTimeout(() => {
      cardWrapper.remove();
      const grid = document.getElementById('favourites-product-grid');
      if (Favourites.count() === 0) {
        const emptyState = document.getElementById('favourites-empty');
        if (emptyState) emptyState.classList.remove('hidden');
        if (grid) grid.innerHTML = '';
        Favourites.setPriceAlertsCtaVisible(false);
      }
    }, 300);
  }

  async function performAdd(provider, numericId, context, gtmItem) {
    const result = provider.add(numericId, context);
    const added = result instanceof Promise ? await result : result;
    if (!added) return false;
    if (gtmItem) pushAddToWishlistGTM(gtmItem);
    return true;
  }

  async function performRemove(provider, numericId, variantId, context) {
    const result = provider.remove(numericId, variantId, context);
    const removed = result instanceof Promise ? await result : result;
    return !!removed;
  }

  class Favourites {
    static get ready() {
      const provider = getProvider();
      return provider ? provider.ready : Promise.resolve();
    }

    static getAll() {
      const provider = getProvider();
      return provider ? provider.getAll() : [];
    }

    static getAllSkus() {
      const provider = getProvider();
      return provider?.getAllSkus ? provider.getAllSkus() : [];
    }

    static has(productId) {
      const provider = getProvider();
      return provider ? provider.has(normalizeProductId(productId)) : false;
    }

    static count() {
      const provider = getProvider();
      return provider ? provider.count() : 0;
    }

    static setPriceAlertsCtaVisible(visible) {
      const cta = document.querySelector('[data-favourites-price-alerts-cta]');
      if (cta) cta.classList.toggle('hidden', !visible);
    }

    static add(productId, context) {
      const provider = getProvider();
      if (!provider) return false;
      return provider.add(normalizeProductId(productId), context);
    }

    static remove(productId) {
      const provider = getProvider();
      if (!provider) return false;
      return provider.remove(normalizeProductId(productId));
    }

    static clear() {
      const provider = getProvider();
      if (!provider) return;
      return provider.clear();
    }
  }

  Favourites.playFavAnimation = function(btn, direction) {
    const cls = direction === 'in' ? 'is-animating-in' : 'is-animating-out';
    btn.classList.remove('is-animating-in', 'is-animating-out');
    void btn.offsetWidth;
    btn.classList.add(cls);
    const icon = btn.querySelector('.product-favourite-btn__icon');
    const cleanup = () => btn.classList.remove(cls);
    let cleaned = false;
    const safeCleanup = () => {
      if (cleaned) return;
      cleaned = true;
      cleanup();
    };
    btn.addEventListener('animationend', safeCleanup, { once: true });
    if (icon) {
      icon.addEventListener('animationend', safeCleanup, { once: true });
    }
    setTimeout(safeCleanup, direction === 'in' ? 500 : 300);
  };

  Favourites.initializeButtons = function() {
    document.querySelectorAll('.product-card-wrapper .product-favourite-btn, #product-favourite-btn').forEach((btn) => {
      if (isButtonBusy(btn)) return;

      let numericId = null;
      let variantId = null;

      if (btn.id === 'product-favourite-btn') {
        const pdpData = getPdpData();
        if (!pdpData) return;
        numericId = String(pdpData.productId);
        variantId = getPdpVariantId(pdpData);
      } else {
        const card = btn.closest('.product-card-wrapper')?.querySelector('.product-card');
        if (!card || !card.dataset.productId) return;
        numericId = normalizeProductId(card.dataset.productId);
      }

      setButtonState(btn, isFavourited(numericId));
    });
  };

  window.Favourites = Favourites;

  document.addEventListener('click', async function(e) {
    const cardBtn = e.target.closest('.product-card-wrapper .product-favourite-btn');
    const pdpBtn = e.target.closest('#product-favourite-btn');
    const btn = cardBtn || pdpBtn;
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    if (isButtonBusy(btn) || btn.classList.contains('is-loading')) return;

    const provider = getProvider();
    if (!provider) return;

    setButtonBusy(btn, true);

    try {
      await provider.ready;

      let numericId = null;
      let variantId = null;
      let context = null;
      let gtmItem = null;
      let cardWrapper = null;

      if (pdpBtn) {
        const pdpData = getPdpData();
        if (!pdpData) return;
        numericId = String(pdpData.productId);
        variantId = getPdpVariantId(pdpData);
        context = {
          type: 'pdp',
          variantId: variantId,
          pdpData: pdpData,
          sku: pdpData.sku || null,
          productSku: pdpData.sku || null,
          priceCents: parsePriceCents(pdpData.price != null ? pdpData.price * 100 : 0),
          currency: config.currency || 'GBP'
        };
        gtmItem = getGtmContextFromPdp(pdpData);
      } else {
        const wrapper = btn.closest('.product-card-wrapper');
        const card = wrapper?.querySelector('.product-card');
        if (!card || !card.dataset.productId) return;
        numericId = normalizeProductId(card.dataset.productId);
        context = getCardContext(card);
        gtmItem = getGtmContextFromCard(card, numericId);
        cardWrapper = wrapper;
      }

      const productKey = getProductKey(numericId, variantId);
      const useOptimistic = usesOptimisticProvider();

      await enqueueProductOp(productKey, async () => {
        const isCurrentlyFavourited = isFavourited(numericId);

        try {
          if (isCurrentlyFavourited) {
            const removeContext = {
              sku: context?.sku || context?.productSku || null,
              productSku: context?.sku || context?.productSku || null
            };

            setButtonState(btn, false);
            Favourites.playFavAnimation(btn, 'out');
            if (useOptimistic) provider.optimisticRemove(numericId);

            const removed = await performRemove(provider, numericId, variantId, removeContext);
            if (!removed) throw new Error('remove failed');

            removeCardFromFavouritesPage(cardWrapper);
            adjustSocialCount(btn, -1);
            console.log('[Favourites] Removed:', numericId);
            return;
          }

          setButtonState(btn, true);
          Favourites.playFavAnimation(btn, 'in');
          if (useOptimistic) provider.optimisticAdd(numericId, context);

          const added = await performAdd(
            provider,
            numericId,
            useOptimistic ? { ...context, skipDuplicateCheck: true } : context,
            gtmItem
          );
          if (!added) throw new Error('add failed');

          adjustSocialCount(btn, 1);
          console.log('[Favourites] Added:', numericId);
        } catch (error) {
          const stillFavourited = isFavourited(numericId);

          if (isCurrentlyFavourited) {
            if (!stillFavourited) {
              setButtonState(btn, true);
              Favourites.playFavAnimation(btn, 'in');
              if (useOptimistic) provider.optimisticAdd(numericId, context);
            }
          } else if (!stillFavourited) {
            setButtonState(btn, false);
            Favourites.playFavAnimation(btn, 'out');
            if (useOptimistic) provider.optimisticRemove(numericId);
          }

          console.warn('[Favourites] Could not update favourites:', numericId, error);
        }
      });
    } finally {
      setButtonBusy(btn, false);
    }
  });

  function getSocialCountFromWrapper(wrapper) {
    if (!wrapper || wrapper.classList.contains('is-social-count-empty')) return 0;

    const valueEl = wrapper.querySelector('.product-favourite-btn__social-count-value');
    if (!valueEl || valueEl.hidden) return 0;

    const count = Number(valueEl.textContent);
    return Number.isFinite(count) ? Math.max(0, count) : 0;
  }

  function applySocialCountState(wrapper, count) {
    const valueEl = wrapper.querySelector('.product-favourite-btn__social-count-value');
    if (!valueEl) return;

    const skeletonEl = wrapper.querySelector('.product-favourite-btn__social-count-skeleton');
    wrapper.classList.remove('is-social-count-ready', 'is-social-count-empty');

    if (count > 0) {
      if (skeletonEl) skeletonEl.remove();
      valueEl.textContent = String(count);
      valueEl.hidden = false;
      valueEl.removeAttribute('hidden');
      wrapper.removeAttribute('aria-hidden');
      wrapper.classList.add('is-social-count-ready');
      return;
    }

    valueEl.hidden = true;
    valueEl.textContent = '';
    wrapper.setAttribute('aria-hidden', 'true');
    wrapper.classList.add('is-social-count-empty');
  }

  function getPdpSocialCountWrapper() {
    const btn = document.getElementById('product-favourite-btn');
    return btn?.querySelector('[data-social-count]') || null;
  }

  function updatePopularBadge(count) {
    const label = document.querySelector('[data-popular-item-label]');
    if (!label) return;
    const isPopular = count > 20;
    label.classList.toggle('is-popular-visible', isPopular);
    label.hidden = !isPopular;
  }

  async function hydratePdpSocialCount() {
    const pdpData = getPdpData();
    if (!pdpData?.productId) return;

    const wrapper = getPdpSocialCountWrapper();
    if (!wrapper) return;

    if (!window.FavouritesAPIV2?.fetchFavouritesCount) {
      console.warn('[Favourites] FavouritesAPIV2 not loaded');
      return;
    }

    try {
      const count = await window.FavouritesAPIV2.fetchFavouritesCount(pdpData.productId);
      pdpSocialCount = count;
      applySocialCountState(wrapper, pdpSocialCount);
      updatePopularBadge(pdpSocialCount);
    } catch (error) {
      console.warn('[Favourites] Could not load PDP social count:', error);
    }
  }

  function adjustSocialCount(btn, delta) {
    if (!delta || !btn) return;

    const wrapper = btn.querySelector('[data-social-count]');
    if (!wrapper) return;

    const isPdp = btn.id === 'product-favourite-btn';

    let current;
    if (isPdp) {
      current = pdpSocialCount !== null ? pdpSocialCount : getSocialCountFromWrapper(wrapper);
    } else {
      current = getSocialCountFromWrapper(wrapper);
    }

    const next = Math.max(0, current + delta);
    if (isPdp) {
      pdpSocialCount = next;
      updatePopularBadge(next);
    }
    applySocialCountState(wrapper, next);
  }

  function initProvider() {
    const provider = getProvider();
    if (!provider) {
      console.error('[Favourites] Provider not loaded for backend:', config.backend);
      return;
    }

    provider.onChange(() => {
      syncCountCookie(Favourites.count());
      dispatchChange();
    });

    hydratePdpSocialCount();

    setButtonsLoading(true);

    provider.ready.then(() => {
      syncCountCookie(Favourites.count());
      setButtonsLoading(false);
      Favourites.initializeButtons();
    });
  }

  document.addEventListener('favourites:change', function() {
    if (window.Favourites?.initializeButtons) {
      window.Favourites.initializeButtons();
    }
  });

  initProvider();
  console.log('[Favourites] Module loaded (' + config.backend + ')');
})();
